/**
 * K-OS Health Today screen. Replaces the placeholder Health.tsx now that
 * F2 + H1-H7 data layers ship.
 *
 * Shows: greeting, profile prompt (if missing), latest weight + body
 * comp, today's check-in (form or summary), nutrition target snapshot,
 * goals digest, recent workouts.
 */

import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '@k-os/ui';
import { useSession } from '../../api/auth-hooks';
import {
  useHealthProfile,
  useHealthGoals,
  useTodayCheckin,
  useMeasurements,
  useNutritionTarget,
  useNutritionDays,
  useWorkouts,
  useActiveWorkouts,
  useSleepSessions,
} from '../../api/health';
import { formatElapsed } from './workouts/elapsed';
import styles from './health.module.css';

export function HealthTodayScreen() {
  const session = useSession();
  const profile = useHealthProfile();
  const goals = useHealthGoals();
  const checkin = useTodayCheckin();
  const weight = useMeasurements('weight');
  const nutritionTarget = useNutritionTarget();
  const nutritionDays = useNutritionDays();
  const workouts = useWorkouts();
  const activeWorkouts = useActiveWorkouts();
  const sleep = useSleepSessions();

  const firstName = session.data?.user?.displayName?.split(' ')[0] ?? 'there';
  const greeting = useMemo(() => greetingFor(new Date()), []);

  const latestWeight = weight.data?.measurements?.[0];
  const todayDate = new Date().toISOString().slice(0, 10);
  const todayNutrition = nutritionDays.data?.days?.find((d) => d.date === todayDate);
  const recentWorkouts = workouts.data?.workouts?.slice(0, 3) ?? [];
  const recentSleep = sleep.data?.sessions?.[0];
  const activeGoals = goals.data?.goals?.filter((g) => g.status !== 'archived') ?? [];
  const activeSessions = activeWorkouts.data?.workouts ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <div className={styles.eyebrow}>{formatToday(new Date())}</div>
          <h2 className={styles.title}>
            {greeting}, {firstName}.
          </h2>
          <div className={styles.subtitle}>
            Health overview. Log a check-in, weight, or workout — the agents
            see what you log and react.
          </div>
        </div>
        <div className={styles.actions}>
          <Link to="/health/checkin" className="kos-btn">
            <Icon name="check" size={12} /> Check-in
          </Link>
          <Link to="/health/measurements" className="kos-btn">
            <Icon name="plus" size={12} /> Log measurement
          </Link>
          <Link to="/health/workouts" className="kos-btn kos-btn-primary">
            <Icon name="arrowRight" size={12} /> Workouts
          </Link>
        </div>
      </header>

      {activeSessions.length > 0 && (
        <Link
          to="/health/workouts"
          search={
            activeSessions.length === 1
              ? { tab: 'today', session: activeSessions[0]!.id }
              : { tab: 'today', choose: true }
          }
          className={styles.card}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 18,
            textDecoration: 'none',
            color: 'inherit',
            borderColor: 'var(--accent)',
          }}
        >
          <span aria-hidden style={{ color: 'var(--accent)' }}>
            {activeSessions.length === 1 && activeSessions[0]!.pausedAt ? '⏸' : '▶'}
          </span>
          <span>
            {activeSessions.length === 1
              ? activeSessions[0]!.name
              : `${activeSessions.length} active workouts`}
            {activeSessions.length === 1 && (
              <>
                {' — '}
                {activeSessions[0]!.pausedAt
                  ? 'Paused'
                  : formatElapsed(activeSessions[0]!.startTime)}
              </>
            )}
          </span>
          <span style={{ marginLeft: 'auto', fontWeight: 600 }}>Resume ›</span>
        </Link>
      )}

      {profile.data === null && !profile.isLoading && (
        <div className={styles.empty}>
          You haven't created your health profile yet.{' '}
          <Link to="/health/profile" className="kos-btn" style={{ marginLeft: 8 }}>
            Set up profile
          </Link>
        </div>
      )}

      <div className={styles.gridWide}>
        {/* Today's check-in card */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Today's check-in</h3>
            <Link to="/health/checkin" className={styles.cardSub}>
              edit ›
            </Link>
          </div>
          {checkin.isLoading ? (
            <div className={styles.muted}>Loading…</div>
          ) : checkin.data ? (
            <div className={styles.kpiRow}>
              {[
                ['Energy', checkin.data.energy],
                ['Mood', checkin.data.mood],
                ['Sleep', checkin.data.sleepQuality],
                ['Stress', checkin.data.stress],
                ['Soreness', checkin.data.soreness],
              ].map(([label, value]) => (
                <div key={label as string} className={styles.kpi}>
                  <div className={styles.kpiLabel}>{label as string}</div>
                  <div>
                    <span className={styles.bigNum}>{value ?? '—'}</span>
                    {value !== null && value !== undefined && (
                      <span className={styles.bigNumUnit}>/ 5</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.muted}>
              Not done yet — <Link to="/health/checkin">log today's check-in</Link>.
            </div>
          )}
        </section>

        {/* Weight / latest measurement */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Latest weight</h3>
            <Link to="/health/measurements" className={styles.cardSub}>
              history ›
            </Link>
          </div>
          {latestWeight ? (
            <>
              <div>
                <span className={styles.bigNum}>{latestWeight.value}</span>
                <span className={styles.bigNumUnit}>{latestWeight.unit ?? 'kg'}</span>
              </div>
              <div className={styles.cardSub} style={{ marginTop: 4 }}>
                {new Date(latestWeight.recordedAt).toLocaleDateString()} ·{' '}
                {latestWeight.source}
              </div>
            </>
          ) : (
            <div className={styles.muted}>
              No weight logged yet —{' '}
              <Link to="/health/measurements">log one</Link>.
            </div>
          )}
        </section>

        {/* Nutrition snapshot */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Nutrition</h3>
            <Link to="/health/nutrition" className={styles.cardSub}>
              open ›
            </Link>
          </div>
          {nutritionTarget.data ? (
            <div className={styles.kpiRow}>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Target</div>
                <div>
                  <span className={styles.bigNum}>
                    {nutritionTarget.data.kcal ?? '—'}
                  </span>
                  <span className={styles.bigNumUnit}>kcal</span>
                </div>
              </div>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Today</div>
                <div>
                  <span className={styles.bigNum}>
                    {todayNutrition?.kcal ?? '—'}
                  </span>
                </div>
              </div>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Protein target</div>
                <div>
                  <span className={styles.bigNum}>
                    {nutritionTarget.data.proteinG ?? '—'}
                  </span>
                  <span className={styles.bigNumUnit}>g</span>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.muted}>
              No nutrition target yet —{' '}
              <Link to="/health/nutrition">set one</Link>.
            </div>
          )}
        </section>

        {/* Sleep */}
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <h3 className={styles.cardTitle}>Last sleep</h3>
            <Link to="/health/sleep" className={styles.cardSub}>
              history ›
            </Link>
          </div>
          {recentSleep ? (
            <div className={styles.kpiRow}>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Total</div>
                <div>
                  <span className={styles.bigNum}>{recentSleep.totalH ?? '—'}</span>
                  <span className={styles.bigNumUnit}>h</span>
                </div>
              </div>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Score</div>
                <div>
                  <span className={styles.bigNum}>{recentSleep.score ?? '—'}</span>
                </div>
              </div>
              <div className={styles.kpi}>
                <div className={styles.kpiLabel}>Date</div>
                <div className={styles.rowMeta}>{recentSleep.date}</div>
              </div>
            </div>
          ) : (
            <div className={styles.muted}>
              No sleep logged. <Link to="/health/sleep">Log one</Link>.
            </div>
          )}
        </section>
      </div>

      {/* Active goals */}
      <section className={styles.card} style={{ marginTop: 18 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Active goals</h3>
          <Link to="/health/goals" className={styles.cardSub}>
            manage ›
          </Link>
        </div>
        {activeGoals.length === 0 ? (
          <div className={styles.muted}>
            No goals yet. <Link to="/health/goals">Create one</Link>.
          </div>
        ) : (
          <div className={styles.list}>
            {activeGoals.slice(0, 5).map((g) => (
              <div key={g.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{g.title}</div>
                  <div className={styles.rowMeta}>
                    {g.type} · {g.targetDate ? `by ${g.targetDate}` : 'no date'}
                    {g.current !== null &&
                      g.current !== undefined &&
                      g.target !== null &&
                      g.target !== undefined && (
                        <>
                          {' '}
                          · {String(g.current)} → {String(g.target)} {g.unit ?? ''}
                        </>
                      )}
                  </div>
                </div>
                <span className={styles.tag}>{g.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Recent workouts */}
      <section className={styles.card} style={{ marginTop: 18 }}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Recent workouts</h3>
          <Link to="/health/workouts" className={styles.cardSub}>
            log ›
          </Link>
        </div>
        {recentWorkouts.length === 0 ? (
          <div className={styles.muted}>
            No workouts yet. <Link to="/health/workouts">Log one</Link>.
          </div>
        ) : (
          <div className={styles.list}>
            {recentWorkouts.map((w) => (
              <div key={w.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>{w.name}</div>
                  <div className={styles.rowMeta}>
                    {w.date} · {w.type}
                    {w.durationMin ? ` · ${w.durationMin} min` : ''}
                    {w.rpe ? ` · RPE ${w.rpe}` : ''}
                  </div>
                </div>
                <span className={styles.tag}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function greetingFor(d: Date): string {
  const hr = d.getHours();
  if (hr < 5) return 'Late night';
  if (hr < 12) return 'Good morning';
  if (hr < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatToday(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
